import { useCallback, useEffect, useState } from "react";

function expandBounds(
  bounds: google.maps.LatLngBounds,
  factor = 0.15
): google.maps.LatLngBounds {
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const latPad = (ne.lat() - sw.lat()) * factor;
  const lngPad = (ne.lng() - sw.lng()) * factor;

  return new google.maps.LatLngBounds(
    { lat: sw.lat() - latPad, lng: sw.lng() - lngPad },
    { lat: ne.lat() + latPad, lng: ne.lng() + lngPad }
  );
}

/**
 * Returns the set of talhão ids whose centroids are inside the current map
 * viewport (with padding). Updates on map idle to avoid work during pan/zoom.
 */
export function useViewportTalhaoIds(
  mapRef: { current: google.maps.Map | null },
  features: readonly { properties?: Record<string, any> | null }[],
  enabled = true
) {
  const [visibleIds, setVisibleIds] = useState<Set<string> | null>(null);

  const syncVisibleIds = useCallback(() => {
    const map = mapRef.current;
    if (!map || !enabled) {
      setVisibleIds(null);
      return;
    }

    const bounds = map.getBounds();
    if (!bounds) return;

    const padded = expandBounds(bounds);
    const next = new Set<string>();

    for (let i = 0; i < features.length; i++) {
      const props = features[i]?.properties;
      const id = props?.id;
      const lat = props?.pontoCentralLat;
      const lng = props?.pontoCentralLong;
      if (!id || lat == null || lng == null) continue;
      if (padded.contains({ lat, lng })) {
        next.add(String(id));
      }
    }

    setVisibleIds((prev) => {
      if (prev && prev.size === next.size) {
        let same = true;
        next.forEach((id) => {
          if (!prev.has(id)) same = false;
        });
        if (same) return prev;
      }
      return next;
    });
  }, [mapRef, features, enabled]);

  useEffect(() => {
    if (!enabled) {
      setVisibleIds(null);
      return;
    }

    const map = mapRef.current;
    if (!map) return;

    syncVisibleIds();
    const listener = map.addListener("idle", syncVisibleIds);
    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [mapRef, enabled, syncVisibleIds, features]);

  return visibleIds;
}
